import { useState, useEffect } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Phone, Mail, MapPin, Send, CheckCircle } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContactPage = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [contactInfo, setContactInfo] = useState(null);
  const [locations, setLocations] = useState([]);
  const [phoneHours, setPhoneHours] = useState("");

  useEffect(() => {
    axios.get(`${API}/page/contact`).then(res => setContactInfo(res.data)).catch(() => {});
    axios.get(`${API}/locations`).then(res => setLocations(res.data.filter(l => l.id !== "head_office"))).catch(() => {});
    // Fetch Head Office hours to show phone availability
    axios.get(`${API}/opening-hours`).then(res => {
      const ho = res.data.find(h => h.location_id === "head_office");
      if (ho) {
        const openDays = ho.hours.filter(h => h.is_open);
        if (openDays.length > 0) {
          const dayNames = openDays.map(d => d.day.slice(0, 3));
          const times = openDays[0];
          // Group consecutive days
          const first = dayNames[0];
          const last = dayNames[dayNames.length - 1];
          const range = dayNames.length > 1 ? `${first}-${last}` : first;
          setPhoneHours(`${range} ${times.open_time}-${times.close_time}`);
        }
      }
    }).catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent! We'll get back to you soon.");
      setSubmitted(true);
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const info = contactInfo || {};

  return (
    <div className="min-h-screen" data-testid="contact-page">
      <Header />
      <main>
        {/* Hero */}
        <section className="py-12 md:py-16 bg-gradient-to-b from-[#fff5f5] to-[#FFFBF5]">
          <div className="container-gymtots text-center">
            <h1 className="text-3xl md:text-4xl font-bold text-[#2D3436] mb-4" style={{ fontFamily: 'Fredoka, cursive' }}>
              Contact <span className="text-[#ff3131]">Us</span>
            </h1>
            <p className="text-[#636E72] max-w-xl mx-auto">{info.subtitle || "Have a question? We'd love to hear from you!"}</p>
          </div>
        </section>

        {/* Contact Methods */}
        <section className="py-12 md:py-16 bg-white">
          <div className="container-gymtots">
            <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto mb-12">
              <a href={`tel:${(info.phone || "02380 455007").replace(/\s/g, "")}`} className="bg-[#FFFBF5] rounded-2xl p-6 text-center hover:shadow-lg transition-shadow" data-testid="contact-call-card">
                <div className="w-14 h-14 rounded-2xl bg-[#ff3131]/10 flex items-center justify-center mx-auto mb-4">
                  <Phone className="w-7 h-7 text-[#ff3131]" />
                </div>
                <h3 className="font-bold text-[#2D3436] mb-1" style={{ fontFamily: 'Fredoka, cursive' }}>Call Us</h3>
                <p className="text-[#ff3131] font-semibold">{info.phone || "02380 455007"}</p>
                {phoneHours && <p className="text-xs text-[#636E72] mt-1">{phoneHours}</p>}
              </a>

              <a href={`mailto:${info.email || "admin@gym-tots.co.uk"}`} className="bg-[#FFFBF5] rounded-2xl p-6 text-center hover:shadow-lg transition-shadow" data-testid="contact-email-card">
                <div className="w-14 h-14 rounded-2xl bg-[#7dbff8]/10 flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-7 h-7 text-[#7dbff8]" />
                </div>
                <h3 className="font-bold text-[#2D3436] mb-1" style={{ fontFamily: 'Fredoka, cursive' }}>Email</h3>
                <p className="text-[#7dbff8] font-semibold">{info.email || "admin@gym-tots.co.uk"}</p>
              </a>
            </div>

            {/* Contact Form */}
            <div className="max-w-2xl mx-auto mb-16">
              <h2 className="text-2xl font-bold text-[#2D3436] text-center mb-8" style={{ fontFamily: 'Fredoka, cursive' }}>
                Send us a <span className="text-[#7dbff8]">Message</span>
              </h2>
              {submitted ? (
                <div className="bg-[#FFFBF5] rounded-2xl p-8 text-center" data-testid="contact-success">
                  <CheckCircle className="w-16 h-16 text-[#7dbff8] mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-[#2D3436] mb-2" style={{ fontFamily: 'Fredoka, cursive' }}>Message Sent!</h3>
                  <p className="text-[#636E72] mb-4">Thank you for getting in touch. We'll get back to you as soon as possible.</p>
                  <button onClick={() => { setSubmitted(false); setForm({ name: "", email: "", phone: "", message: "" }); }} className="text-[#ff3131] font-semibold hover:underline text-sm" data-testid="send-another-btn">Send another message</button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-[#2D3436] mb-1.5">Your Name *</label>
                      <input type="text" required value={form.name} onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))} className="w-full p-3 border-2 border-gray-100 rounded-xl focus:border-[#7dbff8] outline-none text-sm" placeholder="Jane Smith" data-testid="contact-name-input" />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-[#2D3436] mb-1.5">Email Address *</label>
                      <input type="email" required value={form.email} onChange={(e) => setForm(p => ({ ...p, email: e.target.value }))} className="w-full p-3 border-2 border-gray-100 rounded-xl focus:border-[#7dbff8] outline-none text-sm" placeholder="jane@example.com" data-testid="contact-email-input" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-[#2D3436] mb-1.5">Phone Number</label>
                    <input type="tel" value={form.phone} onChange={(e) => setForm(p => ({ ...p, phone: e.target.value }))} className="w-full p-3 border-2 border-gray-100 rounded-xl focus:border-[#7dbff8] outline-none text-sm" placeholder="07123 456789 (optional)" data-testid="contact-phone-input" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-[#2D3436] mb-1.5">Message *</label>
                    <textarea required rows={5} value={form.message} onChange={(e) => setForm(p => ({ ...p, message: e.target.value }))} className="w-full p-3 border-2 border-gray-100 rounded-xl focus:border-[#7dbff8] outline-none text-sm resize-none" placeholder="How can we help you?" data-testid="contact-message-input" />
                  </div>
                  <button type="submit" disabled={isSubmitting} className="w-full bg-[#ff3131] hover:bg-[#e62e2e] text-white py-3.5 rounded-full font-bold text-sm flex items-center justify-center gap-2 transition-colors disabled:opacity-60" style={{ fontFamily: 'Fredoka, cursive' }} data-testid="contact-submit-btn">
                    {isSubmitting ? (<><div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> Sending...</>) : (<><Send className="w-4 h-4" /> Send Message</>)}
                  </button>
                </form>
              )}
            </div>

            {/* Locations from API */}
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl font-bold text-[#2D3436] text-center mb-8" style={{ fontFamily: 'Fredoka, cursive' }}>
                Our <span className="text-[#7dbff8]">Locations</span>
              </h2>
              <div className="grid md:grid-cols-2 gap-6">
                {locations.map((loc, i) => {
                  const color = i % 2 === 0 ? "#ff3131" : "#7dbff8";
                  return (
                    <div key={loc.id} className="bg-[#FFFBF5] rounded-2xl p-6">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${color}15` }}>
                          <MapPin className="w-5 h-5" style={{ color }} />
                        </div>
                        <h3 className="font-bold text-[#2D3436]" style={{ fontFamily: 'Fredoka, cursive' }}>{loc.display_name}</h3>
                      </div>
                      <p className="text-[#636E72] text-sm mb-3">{loc.address}<br/>{loc.postcode}</p>
                      <a href={loc.map_url || `https://maps.google.com/?q=${encodeURIComponent(loc.postcode)}`} target="_blank" rel="noopener noreferrer" className="inline-block mt-2 font-semibold text-sm hover:underline" style={{ color }}>Get Directions</a>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default ContactPage;
